import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';

import { INodeBasePanelElementConfig } from '../NodeBase/NodeBase';

/* eslint-disable react/no-unused-prop-types */

export interface INodeInnerCardProps {
    children?: React.ReactNode;
    panelSubtitle?: string;
    panelElementConfigs?: INodeBasePanelElementConfig[];
    panelClassName?: string;
    actionButtons?: JSX.Element[];
    active?: boolean;
    error?: boolean;
    editLabel?: string;
    hideCard?: boolean; // when true, the card wrapper is hidden and only the content is rendered
    onRemove?: () => void;
    onSelect?: () => void;
    onApply?: () => void;
}

export const NodeInnerCard = (props: INodeInnerCardProps) => {
    const { onRemove } = props;

    /**
     * determine if a page / sub-node contains errors
     */
    const isError = useMemo(
        () => props.error || !!props.panelElementConfigs?.some(config => config.validate?.() === false),
        [props.error, props.panelElementConfigs],
    );

    /**
     * the remove button is embedded in the card, therefore it needs to handle the event
     */
    const handleRemove = useCallback((e: React.MouseEvent) => {
        onRemove && e.stopPropagation(); // do not block the click even if the remove button is disabled
        onRemove?.();
    }, [onRemove]);

    if (!props.children) {
        return null; // card content is mandatory; do nothing if it is missing
    }

    if (props.hideCard && React.isValidElement(props.children)) {
        return <>{React.cloneElement(props.children, { onClick: props.onSelect })}</>; // render only the content without the card wrapper
    }

    const classname = classNames('workflow-node-inner-card', {
        [`workflow-node-inner-card-${props.panelClassName}`]: props.panelClassName,
        active: props.active,
        error: isError,
    });
    return (
        <div data-testid='workflow-node-inner-card' className={classname} onClick={props.onSelect}>
            {props.children}

            {/* node inner card generates the button row programmatically */}
            <div className='workflow-service-action-node-link-container'>
                {props.editLabel ?? i18n.gettext('Edit')}
                <a type='button' role='button' onClick={handleRemove} className={props.onRemove ? '' : 'disabled'}>
                    {i18n.gettext('Remove')}
                </a>
            </div>
        </div>
    );
};
